import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useAuthProvider } from "../Authentication/AuthProviderSwitch";
import {
  cancelClientSubscription,
  getClientSubscriptionInvoices,
  getClientSubscriptions,
  setPaymentsApiTokenProvider,
} from "../../services/paymentsApi";

const ClientSubscriptionsPage = () => {
  const toast = useToast();
  const { getToken, isAuthenticated } = useAuthProvider();

  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isCanceling, setIsCanceling] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !getToken) {
      setPaymentsApiTokenProvider(null);
      return;
    }

    setPaymentsApiTokenProvider(() => getToken());
    return () => setPaymentsApiTokenProvider(null);
  }, [getToken, isAuthenticated]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [subscriptionsData, invoiceData] = await Promise.all([
        getClientSubscriptions(),
        getClientSubscriptionInvoices(),
      ]);

      setSubscriptions(Array.isArray(subscriptionsData) ? subscriptionsData : []);
      setInvoices(Array.isArray(invoiceData) ? invoiceData : []);
    } catch (err) {
      setError(err.message || "Failed to load subscription data.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadData();
  }, [isAuthenticated, loadData]);

  const handleCancel = async (subscriptionId) => {
    setIsCanceling(true);
    try {
      const updated = await cancelClientSubscription(subscriptionId);
      setSubscriptions((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
      toast({ title: "Subscription canceled", status: "success" });
    } catch (err) {
      toast({ title: "Cancel failed", description: err.message, status: "error" });
    } finally {
      setIsCanceling(false);
    }
  };

  const statusColor = (status) => {
    const normalized = String(status || "").toUpperCase();
    if (normalized === "ACTIVE" || normalized === "PAID") return "green";
    if (normalized === "PENDING") return "yellow";
    if (normalized === "CANCELED" || normalized === "REFUNDED") return "orange";
    if (normalized === "FAILED") return "red";
    return "gray";
  };

  const parseAmount = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const formatCurrency = (amount, currency = "USD") => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return "—";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (error) {
      return `${currency} ${value.toFixed(2)}`;
    }
  };

  const metrics = useMemo(() => {
    const activeSubscriptions = subscriptions.filter(
      (entry) => String(entry.status || "").toUpperCase() === "ACTIVE"
    );

    const monthlyRecurring = activeSubscriptions.reduce((sum, entry) => {
      const recurring =
        entry?.plan?.monthlyPrice ??
        entry?.plan?.amount ??
        entry?.planAmount ??
        entry?.monthlyAmount ??
        0;
      return sum + parseAmount(recurring);
    }, 0);

    const paidInvoices = invoices.filter(
      (invoice) => String(invoice.status || "").toUpperCase() === "PAID"
    );
    const paidTotal = paidInvoices.reduce(
      (sum, invoice) => sum + parseAmount(invoice.amountPaid ?? invoice.amountDue),
      0
    );

    const outstandingInvoices = invoices.filter((invoice) => {
      const status = String(invoice.status || "").toUpperCase();
      return status !== "PAID" && status !== "REFUNDED" && status !== "CANCELED";
    });
    const outstandingTotal = outstandingInvoices.reduce(
      (sum, invoice) => sum + parseAmount(invoice.amountDue ?? invoice.amountPaid),
      0
    );

    const currency = invoices[0]?.currency || activeSubscriptions[0]?.currency || "USD";

    return {
      activeCount: activeSubscriptions.length,
      monthlyRecurring,
      paidTotal,
      outstandingTotal,
      currency,
    };
  }, [invoices, subscriptions]);

  return (
    <Stack spacing={6}>
      <Box>
        <Heading size="md" mb={2}>Subscription Management</Heading>
        <Text color="gray.600" fontSize="sm">
          Track active subscriptions, invoice history, and cancel subscriptions when needed.
        </Text>
      </Box>

      <HStack>
        <Button size="sm" variant="outline" onClick={loadData} isLoading={isLoading}>
          Refresh
        </Button>
        <Text fontSize="sm" color="gray.500">{subscriptions.length} subscriptions</Text>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
          <Text fontSize="xs" color="gray.500">Active subscriptions</Text>
          <Text fontSize="xl" fontWeight="700">{metrics.activeCount}</Text>
        </Box>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
          <Text fontSize="xs" color="gray.500">MRR (estimated)</Text>
          <Text fontSize="xl" fontWeight="700">{formatCurrency(metrics.monthlyRecurring, metrics.currency)}</Text>
        </Box>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
          <Text fontSize="xs" color="gray.500">Total paid invoices</Text>
          <Text fontSize="xl" fontWeight="700">{formatCurrency(metrics.paidTotal, metrics.currency)}</Text>
        </Box>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
          <Text fontSize="xs" color="gray.500">Outstanding invoices</Text>
          <Text fontSize="xl" fontWeight="700">{formatCurrency(metrics.outstandingTotal, metrics.currency)}</Text>
        </Box>
      </SimpleGrid>

      {error ? (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      ) : null}

      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Customer</Th>
              <Th>Subscription ID</Th>
              <Th>Status</Th>
              <Th>Plan</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={5}>
                  <HStack><Spinner size="sm" /><Text>Loading subscriptions...</Text></HStack>
                </Td>
              </Tr>
            ) : subscriptions.length > 0 ? (
              subscriptions.map((entry) => (
                <Tr key={entry.id}>
                  <Td>
                    <Text fontWeight="semibold">{entry.customer?.name || "-"}</Text>
                    <Text fontSize="xs" color="gray.500">{entry.customer?.email || "-"}</Text>
                  </Td>
                  <Td>{entry.stripeSubscriptionId || entry.id}</Td>
                  <Td>
                    <Badge colorScheme={statusColor(entry.status)}>{entry.status || "UNKNOWN"}</Badge>
                  </Td>
                  <Td>{entry.plan?.name || entry.plan?.id || "-"}</Td>
                  <Td>
                    {String(entry.status || "").toUpperCase() === "ACTIVE" ? (
                      <Button
                        size="xs"
                        colorScheme="orange"
                        variant="outline"
                        onClick={() => handleCancel(entry.id)}
                        isLoading={isCanceling}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={5}>
                  <Text color="gray.500">No subscriptions found.</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Box>
        <Heading size="sm" mb={3}>Recent Subscription Invoices</Heading>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Invoice</Th>
                <Th>Customer Email</Th>
                <Th>Status</Th>
                <Th isNumeric>Amount</Th>
              </Tr>
            </Thead>
            <Tbody>
              {invoices.slice(0, 8).map((invoice) => (
                <Tr key={invoice.id}>
                  <Td>{invoice.externalInvoiceId || invoice.id}</Td>
                  <Td>{invoice.customerEmail || "-"}</Td>
                  <Td>
                    <Badge colorScheme={statusColor(invoice.status)}>{invoice.status || "UNKNOWN"}</Badge>
                  </Td>
                  <Td isNumeric>{formatCurrency(invoice.amountPaid || invoice.amountDue || 0, invoice.currency || metrics.currency)}</Td>
                </Tr>
              ))}
              {invoices.length === 0 ? (
                <Tr>
                  <Td colSpan={4}>
                    <Text color="gray.500">No subscription invoice records yet.</Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Stack>
  );
};

export default ClientSubscriptionsPage;
